import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projects } from '../data/projects';
import { publications } from '../data/publications';
import { curriculumData } from '../data/curriculum';

const pages = [
    { id: 'home', name: 'Home', path: '/', icon: '⌂' },
    { id: 'curr', name: 'Curriculum', path: '/curriculum', icon: '🎓' },
    { id: 'proj', name: 'Projects', path: '/projects', icon: '💻' },
    { id: 'pub', name: 'Publications', path: '/publications', icon: '📄' },
];

const EXTERNAL_LINKS = {
    github: 'https://github.com/SalvatoreLS',
    linkedin: 'https://www.linkedin.com/in/salvatorels/',
    scholar: 'https://scholar.google.com/citations?user=TzWJ0VQAAAAJ&hl=en',
    email: 'mailto:salvatore.losardo@ikmail.com',
    mail: 'mailto:salvatore.losardo@ikmail.com',
};

function buildHelpOutput() {
    return [
        'Available commands:',
        '',
        '  whoami              — identity card',
        '  ls                  — list all projects',
        '  ls projects/        — list all projects',
        '  ls publications/    — list publications',
        '  ls cv/              — list CV entries',
        '  cat about.md        — bio',
        '  cat cv.md           — education & experience highlights',
        '  grep <term>         — search projects by tech or keyword',
        '  skills              — top technologies by project count',
        '  open github         — open GitHub profile',
        '  open linkedin       — open LinkedIn',
        '  open scholar        — open Google Scholar profile',
        '  open email          — open email client',
        '  clear               — clear terminal history',
        '  help                — show this message',
    ].join('\n');
}

function buildWhoamiOutput() {
    return [
        'Salvatore Lo Sardo',
        'Role    : MSc AI student @ UvA',
        'Location: Amsterdam, NL',
        'Email   : salvatore.losardo@ikmail.com',
        'GitHub  : github.com/SalvatoreLS',
        'LinkedIn: linkedin.com/in/salvatorels',
        'Scholar : scholar.google.com/citations?user=TzWJ0VQAAAAJ',
        '',
        'MSc Artificial Intelligence @ University of Amsterdam',
        'BSc Computer Engineering @ Politecnico di Torino',
    ].join('\n');
}

function buildLsOutput(arg) {
    const target = (arg || '').replace(/\/$/, '').toLowerCase();

    if (!target || target === 'projects') {
        const lines = ['projects/:', ''];
        projects.forEach(p => {
            const state = p.state === 'Completed' ? '✓' : '…';
            lines.push(`  ${state}  ${p.id.padEnd(28)} ${p.name}`);
        });
        return lines.join('\n');
    }

    if (target === 'publications') {
        const lines = ['publications/:', ''];
        publications.forEach(p => {
            lines.push(`  ${p.id.padEnd(28)} ${p.title}`);
        });
        return lines.join('\n');
    }

    if (target === 'cv') {
        const lines = ['cv/:', ''];
        curriculumData.forEach(e => {
            lines.push(`  [${e.type.padEnd(10)}]  ${e.date.padEnd(22)} ${e.title}`);
        });
        return lines.join('\n');
    }

    return `ls: cannot access '${arg}': No such file or directory`;
}

function buildCatOutput(file) {
    const f = (file || '').toLowerCase();

    if (f === 'about.md' || f === 'about') {
        return [
            "Hello! I'm a passionate engineer with a foundation in **Computer Engineering**",
            'from Politecnico di Torino, currently pursuing a **Master\'s in Artificial Intelligence**',
            'at the University of Amsterdam (UvA).',
            '',
            'My work spans ML research, RAG pipelines, robotics (ROS2),',
            'and agentic AI systems. I\'ve worked at SmartData@Polito, competed in hackathons',
            'across Europe, and served as Vice Coordinator at IEEE-HKN Mu Nu.',
        ].join('\n');
    }

    if (f === 'cv.md' || f === 'cv' || f === 'resume.md' || f === 'resume') {
        const edu = curriculumData.filter(e => e.type === 'Education');
        const exp = curriculumData.filter(e => e.type === 'Experience');
        const awards = curriculumData.filter(e => e.type === 'Award');

        const lines = ['# Curriculum Vitae', '', '## Education'];
        edu.forEach(e => lines.push(`  • ${e.date}  ${e.title} — ${e.subtitle}`));
        lines.push('', '## Experience');
        exp.forEach(e => lines.push(`  • ${e.date}  ${e.title} — ${e.subtitle}`));
        lines.push('', '## Awards');
        awards.forEach(e => lines.push(`  • ${e.date}  ${e.title}`));
        return lines.join('\n');
    }

    return `cat: ${file}: No such file or directory`;
}

function buildGrepOutput(term) {
    if (!term) return 'grep: missing search term';
    const q = term.toLowerCase();
    const matched = projects.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        (p.technologies || []).some(t => t.toLowerCase().includes(q)) ||
        (p.languages || []).some(l => l.toLowerCase().includes(q)) ||
        (p.categories || []).some(c => c.toLowerCase().includes(q))
    );
    if (matched.length === 0) return `grep: no projects match '${term}'`;
    const lines = [`grep results for '${term}':`, ''];
    matched.forEach(p => lines.push(`  ${p.id.padEnd(28)} ${p.name}`));
    return lines.join('\n');
}

function buildSkillsOutput() {
    const counts = {};
    projects.forEach(p => {
        [...(p.technologies || []), ...(p.languages || [])].forEach(t => {
            counts[t] = (counts[t] || 0) + 1;
        });
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 12);
    const max = sorted[0]?.[1] || 1;
    const lines = ['Top skills by project count:', ''];
    sorted.forEach(([name, n]) => {
        const bar = '█'.repeat(Math.round((n / max) * 12)).padEnd(12, '░');
        lines.push(`  ${bar}  ${n.toString().padStart(2)}  ${name}`);
    });
    return lines.join('\n');
}

function runCommand(raw) {
    const trimmed = raw.trim();
    if (!trimmed) return { type: 'output', text: '' };

    const [cmd, ...args] = trimmed.split(/\s+/);

    switch (cmd.toLowerCase()) {
        case 'help':
            return { type: 'output', text: buildHelpOutput() };
        case 'whoami':
            return { type: 'output', text: buildWhoamiOutput() };
        case 'ls':
            return { type: 'output', text: buildLsOutput(args[0]) };
        case 'cat':
            return { type: 'output', text: buildCatOutput(args[0]) };
        case 'grep':
            return { type: 'output', text: buildGrepOutput(args.join(' ')) };
        case 'skills':
            return { type: 'output', text: buildSkillsOutput() };
        case 'open': {
            const target = (args[0] || '').toLowerCase();
            const url = EXTERNAL_LINKS[target];
            if (url) return { type: 'navigate', url };
            return { type: 'output', error: true, text: `open: unknown target '${args[0]}'. Try: github, linkedin, scholar, email` };
        }
        case 'clear':
            return { type: 'clear' };
        default:
            return { type: 'output', error: true, text: `bash: ${cmd}: command not found` };
    }
}

export default function CommandPalette({ isOpen, setIsOpen }) {
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [terminalHistory, setTerminalHistory] = useState([]);
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const terminalRef = useRef(null);

    const terminalMode = query.startsWith('>');
    const terminalInput = terminalMode ? query.slice(1).trimStart() : '';

    useEffect(() => {
        if (isOpen && inputRef.current) inputRef.current.focus();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setQuery('');
            setSelectedIndex(0);
            setTerminalHistory([]);
        }
    }, [isOpen]);

    useEffect(() => {
        if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
    }, [terminalHistory]);

    const filteredItems = useMemo(() => {
        if (terminalMode) return [];
        const q = query.toLowerCase();
        const matchedPages = pages.filter(p => p.name.toLowerCase().includes(q));
        const matchedProjects = projects.filter(p =>
            p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
        ).map(p => ({ id: p.id, name: p.name, path: `/projects/${p.id}`, icon: '📁', type: 'Project' }));
        const matchedPublications = publications.filter(p =>
            p.title.toLowerCase().includes(q) || p.abstract.toLowerCase().includes(q)
        ).map(p => ({ id: p.id, name: p.title, path: `/publications#${p.id}`, icon: '📄', type: 'Publication' }));
        return [...matchedPages, ...matchedProjects, ...matchedPublications];
    }, [query, terminalMode]);

    const execTerminalInput = useCallback(() => {
        const result = runCommand(terminalInput);
        if (result.type === 'clear') {
            setTerminalHistory([]);
            setQuery('>');
            return;
        }
        if (result.type === 'navigate') {
            window.open(result.url, '_blank', 'noopener,noreferrer');
            setTerminalHistory(h => [...h, { input: terminalInput, output: `Opening ${result.url} ...`, error: false }]);
            setQuery('>');
            return;
        }
        setTerminalHistory(h => [...h, { input: terminalInput, output: result.text, error: !!result.error }]);
        setQuery('>');
    }, [terminalInput]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (!isOpen) return;

            if (e.key === 'Escape') { setIsOpen(false); return; }

            if (terminalMode) {
                if (e.key === 'Enter') { e.preventDefault(); execTerminalInput(); }
                return;
            }

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(i => (i + 1) % Math.max(filteredItems.length, 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(i => (i - 1 + Math.max(filteredItems.length, 1)) % Math.max(filteredItems.length, 1));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (filteredItems[selectedIndex]) {
                    navigate(filteredItems[selectedIndex].path);
                    setIsOpen(false);
                }
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, selectedIndex, filteredItems, navigate, setIsOpen, terminalMode, execTerminalInput]);

    if (!isOpen) return null;

    const run = (item) => { navigate(item.path); setIsOpen(false); };

    return (
        <div className="cmd-overlay" onClick={() => setIsOpen(false)}>
            <div className="cmd-palette" onClick={e => e.stopPropagation()}>

                {/* Input row */}
                <div className="cmd-input-row">
                    {terminalMode ? (
                        <span className="cmd-input-prefix">$</span>
                    ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                        </svg>
                    )}
                    <input
                        ref={inputRef}
                        className="cmd-input"
                        placeholder={terminalMode ? 'type a command… (try: help)' : 'Search pages or projects…'}
                        value={query}
                        onChange={e => { setQuery(e.target.value); setSelectedIndex(0); }}
                    />
                    <button
                        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-dim)', fontSize: 10, padding: '2px 6px', cursor: 'pointer' }}
                        onClick={() => setIsOpen(false)}
                    >
                        Esc
                    </button>
                </div>

                {/* Terminal mode output */}
                {terminalMode && (
                    <>
                        <div className="cmd-terminal" ref={terminalRef}>
                            {terminalHistory.length === 0 && (
                                <div style={{ color: 'var(--text-dim)', marginBottom: 6 }}>
                                    # Terminal mode — type <span style={{ color: 'var(--accent)' }}>help</span> for available commands
                                </div>
                            )}
                            {terminalHistory.map((h, i) => (
                                <div key={i} className="cmd-term-line">
                                    <span className="cmd-term-prompt">$ </span>
                                    <span className="cmd-term-input-text">{h.input}</span>
                                    {h.output && (
                                        <div className={`cmd-term-output${h.error ? ' error' : ''}`}>
                                            {h.output}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="cmd-term-hint">
                            tip: type <span style={{ color: 'var(--accent)' }}>&gt;</span> to enter terminal mode · delete <span style={{ color: 'var(--accent)' }}>&gt;</span> to return to search
                        </div>
                    </>
                )}

                {/* Search mode results */}
                {!terminalMode && (
                    <div className="cmd-results">
                        {filteredItems.length === 0
                            ? <div className="cmd-empty">No results found</div>
                            : filteredItems.map((item, i) => (
                                <button
                                    key={item.path}
                                    className={`cmd-item ${i === selectedIndex ? 'selected' : ''}`}
                                    onMouseEnter={() => setSelectedIndex(i)}
                                    onClick={() => run(item)}
                                >
                                    <span className="cmd-item-icon">{item.icon}</span>
                                    {item.name}
                                    {item.type && <span style={{ fontSize: '11px', color: 'var(--text-dim)', marginLeft: '6px' }}>{item.type}</span>}
                                </button>
                            ))
                        }
                    </div>
                )}

                {/* Footer */}
                <div className="cmd-footer">
                    {terminalMode ? (
                        <>
                            <span><span className="cmd-key">↵</span> Run</span>
                            <span><span className="cmd-key">Esc</span> Close</span>
                        </>
                    ) : (
                        <>
                            <span><span className="cmd-key">↑↓</span> Navigate</span>
                            <span><span className="cmd-key">↵</span> Select</span>
                            <span><span className="cmd-key">Esc</span> Close</span>
                            <span style={{ marginLeft: 'auto', color: 'var(--text-dim)' }}>
                                type <span className="cmd-key">&gt;</span> for terminal
                            </span>
                        </>
                    )}
                </div>

            </div>
        </div>
    );
}
